/**
 * Toolbar row for DRI Insights (EmployeeWise): title, clear filters, optional trailing actions.
 * Value-type (Total DRI, etc.) lives beside Filter Columns on `IndividualDetailsTable`.
 * Shared by `IndividualDetailsSectionHeader` and `EmployeeWiseDriInsightsPanel`.
 */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Clear as ClearIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import type { DRIValueType } from '../../types/driInsights';

/** Options for the EmployeeWise grid value-type dropdown (beside Filter Columns in `IndividualDetailsTable`). */
export const DRI_VALUE_TYPE_SELECT_OPTIONS: { value: DRIValueType; label: string }[] = [
  { value: 'total_dri', label: 'Total DRI' },
  { value: 'certification', label: 'Technical Certification' },
  { value: 'labs', label: 'Labs' },
  { value: 'projects', label: 'Projects Deployed' },
  { value: 'skill_badges', label: 'Skill Badges' },
  { value: 'other_cloud', label: 'Other Cloud Experience' },
  { value: 'training', label: 'Technical Training' },
];

export interface DriEmployeeWiseSectionToolbarProps {
  onClearAllFilters: () => void;
  activeFilterCount: number;
  hideTitle?: boolean;
  flushChrome?: boolean;
  trailingActions?: React.ReactNode;
}

export function DriEmployeeWiseSectionToolbar({
  onClearAllFilters,
  activeFilterCount,
  hideTitle = false,
  flushChrome = false,
  trailingActions,
}: DriEmployeeWiseSectionToolbarProps): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: flushChrome ? 1 : 2,
        mt: hideTitle ? 0 : flushChrome ? 0 : 3,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: '1 1 auto' }}>
        {!hideTitle && (
          <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
            DRI Insights (EmployeeWise)
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {activeFilterCount > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearAllFilters}
            sx={{
              color: SEARCE_COLORS.primary,
              borderColor: SEARCE_COLORS.primary,
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
              },
            }}
          >
            Clear All Filters ({activeFilterCount})
          </Button>
        )}
        {trailingActions}
      </Box>
    </Box>
  );
}
